import { Injectable } from '@angular/core';
import {
  filter,
  fromEvent,
  map,
  Observable,
  of,
  Subject,
  takeUntil,
} from 'rxjs';

import { KeyInteractionUtils } from '@shared/utils/key-interaction-utils';
import {
  HotKey,
  IHotKeySettings,
  KeyActionName,
  KeyEventType,
  KeyEventTypes,
  HotKeySettings,
  UpdateHotKeySettings,
  IKeyEventMessage,
  IKeyEventData,
  CompositeKeys,
} from '@viewer/data';

@Injectable()
export class KeyInteractionService {
  public isDialogOpen: boolean = false;
  successLog: string;
  private _keyEvents: Record<KeyEventType, Subject<IKeyEventData>> = {
    keydown: new Subject<IKeyEventData>(),
    keyup: new Subject<IKeyEventData>(),
  };
  private _keysSettingsData = new Map<HotKey, IHotKeySettings[]>();
  private _keysRelations = new Map<KeyActionName, HotKey>();
  private _unsubscribe$!: Subject<void>;

  init(): void {
    this._unsubscribe$ = new Subject<void>();
    this._initializeKeyListeners();
  }

  destroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
    this._keysSettingsData.clear();
    this._keysRelations.clear();
  }

  public initializeHotKey<T extends KeyActionName>(
    listeningKey: HotKey,
    keySettings: IHotKeySettings<T>
  ): Observable<IKeyEventMessage> {
    if (this._keysRelations.has(keySettings.keyActionName)) {
      this.successLog = `Hotkey for action ${keySettings.keyActionName} is already initialized.`;
      return of();
    }

    this._addKeySettings(listeningKey, keySettings);

    return this._keyEvents[keySettings.keyEventType].asObservable().pipe(
      filter((keyEventData) => {
        return keyEventData.suitableActionNames.includes(
          keySettings.keyActionName
        );
      }),
      map((keyEventData) => {
        return {
          ...keyEventData,
          suitableActionNames: undefined,
        };
      })
    );
  }

  public setActivateState<T extends KeyActionName>(
    keyActionName: T,
    isActive: boolean
  ): void {
    this._replaceKeySettings(keyActionName, { isActive: isActive });
  }

  public removeKeySettings<T extends KeyActionName>(keyActionName: T): void {
    this._replaceKeySettings(keyActionName);
  }

  public removeKeySettingsList<T extends KeyActionName[]>(
    keyActionNameList: T
  ): void {
    for (const keyActionName of keyActionNameList) {
      this._replaceKeySettings(keyActionName);
    }
  }

  /**
   * Performs removing or updating settings data (excluding 'keyActionName')
   */
  private _replaceKeySettings<T extends KeyActionName>(
    keyActionName: T,
    newSettings?: UpdateHotKeySettings<T>
  ): void {
    const key = this._keysRelations.get(keyActionName);
    if (!key) {
      return;
    }

    const keySettingsList = this._keysSettingsData.get(key);

    if (!keySettingsList) {
      return;
    }

    const keySettingsIndex = keySettingsList.findIndex(
      (settings) => settings.keyActionName === keyActionName
    );

    if (
      keySettingsIndex === -1 ||
      (newSettings &&
        !this._checkIsSettingsChangeNeeded(
          keySettingsList[keySettingsIndex],
          newSettings
        ))
    ) {
      return;
    }

    if (newSettings) {
      keySettingsList.splice(
        keySettingsIndex,
        1,
        new HotKeySettings<T>({
          ...keySettingsList[keySettingsIndex],
          keyActionName: keyActionName,
          ...newSettings,
        })
      );
    } else {
      this._keysRelations.delete(
        keySettingsList[keySettingsIndex].keyActionName
      );
      keySettingsList.splice(keySettingsIndex, 1);
    }
  }

  private _setKeyEventData(keyEventData: IKeyEventData): void {
    this._keyEvents[keyEventData.eventType].next(keyEventData);
  }

  private _checkIsSettingsChangeNeeded(
    settings: IHotKeySettings,
    newSettigs: Partial<IHotKeySettings>
  ): boolean {
    for (const newSettigKey of Object.keys(newSettigs)) {
      if (newSettigs[newSettigKey] !== settings[newSettigKey]) {
        return true;
      }
    }
    return false;
  }

  private _initializeKeyListeners(): void {
    for (const keyEvent of KeyEventTypes) {
      fromEvent(window, keyEvent)
        .pipe(
          map((event) => event as KeyboardEvent),
          takeUntil(this._unsubscribe$)
        )
        .subscribe((event) => {
          if (event.repeat || this.isDialogOpen) {
            return;
          }

          const newKeyEventData = this._defineKeyEventData(event, keyEvent);
          if (
            newKeyEventData.hotKey &&
            newKeyEventData.suitableActionNames.length
          ) {
            this._setKeyEventData(newKeyEventData);
          }
        });
    }
  }

  private _defineKeyEventData(
    event: KeyboardEvent,
    eventType: KeyEventType
  ): IKeyEventData {
    const hotKey = Object.values(HotKey).includes(event.code as HotKey)
      ? event.code
      : '';
    const compositeHotKeys = KeyInteractionUtils.defineCompositeKeys(event);
    const suitableActionNames = hotKey
      ? this._defineSuitableKeyActionNames(
          hotKey as HotKey,
          compositeHotKeys,
          eventType
        )
      : [];

    return {
      hotKey: hotKey,
      compositeKeys: compositeHotKeys,
      eventType: eventType,
      suitableActionNames: suitableActionNames,
    };
  }

  private _defineSuitableKeyActionNames(
    hotKey: HotKey,
    compositeKeys: CompositeKeys[],
    eventType: KeyEventType
  ): KeyActionName[] {
    if (!hotKey) {
      return [];
    }
    const keySettings = this._keysSettingsData.get(hotKey);

    if (!keySettings) {
      return [];
    }

    return keySettings
      .reduce((result: IHotKeySettings[], current) => {
        if (
          !current.isActive ||
          current.keyEventType !== eventType ||
          !this._checkIsSuitableByCompositeKeys(current, compositeKeys)
        ) {
          return result;
        }

        const resultElement = result[0];

        if (!resultElement || resultElement.priority === current.priority) {
          result.push(current);
        } else if (resultElement.priority < current.priority) {
          result = [];
          result.push(current);
        }

        return result;
      }, [])
      .map((settings) => settings.keyActionName);
  }

  private _checkIsSuitableByCompositeKeys(
    keySettings: IHotKeySettings,
    compositeKeys: CompositeKeys[]
  ): boolean {
    if (keySettings.compositeKeys.length !== compositeKeys.length) {
      return false;
    }

    const compositeKeysForCheck = [...compositeKeys];

    for (const compositeKey of keySettings.compositeKeys) {
      const compositeKeyIndex = compositeKeysForCheck.findIndex(
        (eventCompositeKey) => eventCompositeKey === compositeKey
      );

      if (compositeKeyIndex === -1) {
        return false;
      }
      compositeKeysForCheck.splice(compositeKeyIndex, 1);
    }

    return compositeKeysForCheck.length === 0;
  }

  private _addKeySettings(key: HotKey, settings: IHotKeySettings): void {
    this._keysRelations.set(settings.keyActionName, key);

    const keySettingsList = this._keysSettingsData.get(key);
    if (!keySettingsList) {
      this._keysSettingsData.set(key, [settings]);
      return;
    }

    keySettingsList.push(settings);
  }
}
